export interface HeadlineKpi {
  key: string;
  moduleKey: string;
  label: string;
  value: string;
  sublabel: string;
  href: string;
}

import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { homeDashboardKeys } from './query-keys';

export interface TrendPoint {
  week: string;
  value: number;
}

export interface RiskIndex {
  score: number;
  maxScore: number;
  /**
   * The organisation's level from the one risk engine (DHA W2-01), against the
   * workspace's thresholds: None (nobody measured), Low, Medium, High,
   * Critical. Optional: an older API sent none.
   */
  level?: string;
  /** The workspace day the risk engine started; the trend has no point before it. */
  engineStartedOn?: string | null;
  deltaPoints: number;
  deltaText: string;
  isGood: boolean;
  topDriver: string;
  trend: TrendPoint[];
}

export interface AttentionItem {
  id: string;
  severity: 'crit' | 'warn' | 'info';
  title: string;
  moduleName: string;
  moduleKey: string;
  meta: string;
  actionLabel: string;
  actionUrl: string;
  isOnboardingPrompt?: boolean;
}

export interface KpiTile {
  value: string;
  deltaText: string;
  comparisonText: string;
  isGood: boolean;
  /** How the figure moved; 'flat' when there is nothing to compare (the API's empty state). */
  trendDirection: 'up' | 'down' | 'flat';
}

export interface DepartmentCompletion {
  department: string;
  completionRate: number;
  totalEnrollments: number;
  completedEnrollments: number;
}

export interface ErRemediationWeek {
  /** The ISO week the point is in ("W38"), in the workspace calendar. */
  week: string;
  /**
   * The point's first workspace day (yyyy-MM-dd): the day itself for daily
   * points, the week's Monday for weekly ones. Optional: an older API sent
   * weeks only.
   */
  period?: string;
  /** Reports received (not colleague copies). */
  reported: number;
  /** Of those, the ones now Closed (the field keeps its old name). */
  autoResolved: number;
}

/** The period every windowed figure counts in, as workspace days. */
export interface HomeWindow {
  /** The preset asked for, or null for chosen days. */
  preset: '7d' | '30d' | '90d' | 'ytd' | null;
  /** First and last workspace day, yyyy-MM-dd, both included. */
  from: string;
  to: string;
  days: number;
}

export interface UpcomingItem {
  id: string;
  day: number;
  month: string;
  title: string;
  moduleName: string;
  meta: string;
}

export interface ModuleTile {
  key: string;
  title: string;
  kpiValue: string;
  kpiLabel: string;
  subtext: string;
  href: string;
  hasData?: boolean;
}

/**
 * A section of the home page, as the API names it (home-dashboard/home-sections.ts):
 * the module key its tiles and attention items carry, plus 'risk'.
 */
export type HomeSection =
  | 'risk'
  | 'phishing'
  | 'smishing'
  | 'vishing'
  | 'phishtrainer'
  | 'lms'
  | 'jit-training'
  | 'announcements'
  | 'gamification'
  | 'er'
  | 'content-studio'
  | 'recipients'
  | 'settings';

export interface DashboardHomeData {
  userName: string;
  companyName: string;
  formattedDate: string;
  /**
   * The sections the caller's ROLE covers — the only ones the API read
   * (custom roles, 2026-10-05). A section not named here was not queried: its
   * object fields below are null, its lists empty, and none of its tiles,
   * headline numbers, attention items or upcoming campaigns are present. Draw
   * nothing for it — not a zero, not a "—". Every built-in role covers every
   * section. Optional: a response from before the field shipped has none, and
   * the page then fails open (the API is what withholds the data).
   */
  shownSections?: HomeSection[];
  /**
   * The workspace zone the API read the date, weeks and tiles in (A08: the
   * header date, the risk and remediation weeks, the upcoming tiles), after
   * its own fallbacks. Optional: a response cached from before the field
   * shipped has none, and the page then re-writes the API's own day instead
   * of guessing a zone.
   */
  timeZone?: string;
  /** Null when the role does not cover the 'risk' section (shownSections). */
  riskIndex: RiskIndex | null;
  attentionItems: AttentionItem[];
  /** Null when the role does not cover 'phishing'. */
  phishProneRate: KpiTile | null;
  /** Null when the role does not cover 'phishing'. */
  reportRate: KpiTile | null;
  /** Null when the role does not cover 'lms'. */
  trainingCompletion: KpiTile | null;
  /** Null when the role does not cover 'phishing'. */
  medianTimeToReport: KpiTile | null;
  /** Empty when the role does not cover 'lms'. */
  departmentCompletion: DepartmentCompletion[];
  departmentTargetRate: number;
  /**
   * One point for every day (up to 31 days) or week of the period; 0 is a real
   * zero. Empty when the role does not cover 'er'.
   */
  erRemediationTrend: ErRemediationWeek[];
  /** How the trend is bucketed. Optional: an older API sent weeks only. */
  erRemediationBucket?: 'day' | 'week';
  /**
   * The period the API counted the windowed figures in — the range picker's
   * choice, validated. Optional: a response cached from before it shipped.
   */
  window?: HomeWindow;
  upcoming: UpcomingItem[];
  moduleTiles: ModuleTile[];
  /**
   * Headline numbers for this tenant, highest-signal module first, composed by
   * the server from the modules it actually has. Optional so a response cached
   * from before the field shipped falls back to the fixed cards instead of
   * rendering an empty row.
   */
  headlineKpis?: HeadlineKpi[];
  /**
   * Module keys this tenant can open. Optional on purpose: a response cached
   * from before the field shipped must fail OPEN (show everything) rather than
   * blank the page — same convention as `useEnabledModules` in the sidebar.
   */
  enabledModules?: string[];
}

/** The range picker's presets, which the API counts in the workspace's days. */
export type HomeRange = '7d' | '30d' | '90d' | 'ytd';

export function useHomeDashboard(options?: { domainId?: string | null; range?: HomeRange }) {
  const { domainId, range = '30d' } = options ?? {};

  return useQuery<DashboardHomeData>({
    queryKey: homeDashboardKeys.home(domainId, range),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (domainId) params.append('domainId', domainId);
      if (range) params.append('range', range);
      const queryStr = params.toString();
      const endpoint = `/dashboard/home${queryStr ? `?${queryStr}` : ''}`;
      return fetchApi<DashboardHomeData>(endpoint);
    },
    staleTime: 60 * 1000,
  });
}
